/**
 * W-9 下单确认(WScreen-9)。productId 经 ?productId= query 读取(与 W-6 product-detail 解耦)。
 * 提交生成 PENDING_PAYMENT 订单(幂等 by clientUuid),成功跳收银台 W-10 透传 state。
 * 字段层硬隔离:绝不渲染抽佣/分账。视觉真源 design/order-preview.html。
 */
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getOrderPreview, submitOrder } from '@/api/order';
import { generateClientUuid } from '@/lib/clientUuid';
import { errorCode, errorMessage } from '@/lib/orderState';

const REMARK_MAX = 200;

/** 提交错误码 → 文案(order-preview.md 五态)。 */
function mapSubmitError(err: unknown): string {
  switch (errorCode(err)) {
    case '020001':
      return '商品不存在';
    case '020003':
      return '商品已下架,无法下单';
    case '030203':
      return '订单提交冲突,请稍后重试';
    case '010100':
      return '登录已过期,请重新登录';
    case 'NETWORK_ERROR':
      return '网络异常,请重试';
    default:
      return errorMessage(err, '提交失败,请稍后重试');
  }
}

export function OrderPreviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const productId = params.get('productId') ?? '';
  // 封面:从商品详情「立即购买」经 state 带入(预览接口不下发封面);直接进入则占位。
  const coverImage = (location.state as { coverImage?: string | null } | null)?.coverImage ?? null;

  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // 幂等键:mount 生成一次,跨重试稳定(FR-C2)。
  const clientUuidRef = useRef(generateClientUuid());
  const submittingRef = useRef(false); // 同步防双击(setState 异步)

  const {
    data: preview,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['order-preview', productId],
    queryFn: () => getOrderPreview(productId),
    enabled: productId !== '',
    retry: 0,
  });

  const handleSubmit = async () => {
    if (!preview || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await submitOrder({
        productId: preview.productId ?? productId,
        remark: remark.trim() || undefined,
        clientUuid: clientUuidRef.current,
      });
      navigate(`/cashier/${resp.orderId}`, {
        state: {
          orderId: resp.orderId,
          amount: resp.amount,
          expiresAt: resp.expiresAt,
          sellerNickname: preview.seller?.nickname,
          sellerAvatar: preview.seller?.avatar ?? null,
        },
      });
      // 成功后页面即将卸载,不复位 ref。
    } catch (err) {
      setSubmitError(mapSubmitError(err));
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  if (!productId) {
    return (
      <div className="inner section">
        <div className="state">
          <p className="muted sm">缺少商品参数,无法确认订单。</p>
          <button type="button" className="btn btn-secondary mt-4" onClick={() => navigate('/')}>
            返回首页
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="inner section">
        <div className="state" data-testid="order-preview-loading">
          <p className="muted sm" style={{ color: 'var(--color-primary)' }}>
            加载中…
          </p>
        </div>
      </div>
    );
  }

  if (isError || !preview) {
    return (
      <div className="inner section">
        <div className="state">
          <p className="sm" style={{ color: 'var(--color-destructive)' }}>
            {mapSubmitError(error)}
          </p>
          <button
            type="button"
            className="btn btn-secondary mt-4"
            onClick={() => navigate(-1)}
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  const seller = preview.seller;
  const amountYuan = formatYuanNoSymbol(preview.amount ?? 0);

  return (
    <div className="inner section" data-testid="order-preview-screen">
      <div className="sm muted mb-6">确认订单</div>
      <h1 className="h2 mb-6">确认订单</h1>

      <div className="checkout-cols">
        {/* 左:商品信息 + 备注 + 履约说明 */}
        <div>
          <div className="card card-pad mb-6">
            <h3 className="h3 mb-4">商品信息</h3>
            <div className="row gap-4">
              <div
                className="card"
                style={{
                  width: 96,
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-muted-foreground)',
                  fontSize: 'var(--text-xs-size)',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                {coverImage ? (
                  <img
                    src={coverImage}
                    alt="商品封面"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  '商品封面'
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'var(--weight-medium)' }}>{preview.productTitle}</div>
                <div className="row gap-2 mt-2">
                  {seller?.avatar ? (
                    <img className="avatar avatar-sm" src={seller.avatar} alt="" />
                  ) : (
                    <span className="avatar avatar-sm" aria-hidden>
                      {seller?.nickname?.slice(0, 1) ?? '·'}
                    </span>
                  )}
                  <span className="sm muted">
                    卖家 {seller?.nickname ?? '我行用户'}
                    {typeof seller?.rating === 'number' ? ` · ⭐ ${seller.rating}` : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="card card-pad mb-6">
            <h3 className="h3 mb-4">买家备注</h3>
            <div className="field" style={{ marginBottom: 0 }}>
              <textarea
                className="textarea"
                maxLength={REMARK_MAX}
                placeholder="选填,如时间约定、当前水平、想达成的目标等(≤200 字)"
                value={remark}
                onChange={(e) => setRemark(e.target.value.slice(0, REMARK_MAX))}
              />
              <div className="hint">备注会同步给卖家,便于 TA 提前准备。</div>
            </div>
          </div>

          <div className="card card-pad">
            <h3 className="h3 mb-4">履约与保障</h3>
            <ul className="sm muted" style={{ listStyle: 'none', lineHeight: 2.1 }}>
              <li>🛡 付款后卖家响应前可申请取消,款项原路退回。</li>
              <li>💬 下单后可在订单内与卖家沟通,确认时间与方式。</li>
              <li>✅ 交易完成后双方互评,资金 T+7 自动结算给卖家。</li>
            </ul>
            {preview.cancelPolicyText && (
              <p className="xs muted mt-4">取消政策:{preview.cancelPolicyText}</p>
            )}
          </div>
        </div>

        {/* 右:金额摘要 + 提交订单 */}
        <aside className="sticky-cta">
          <div className="card card-pad">
            <h3 className="h3 mb-4">金额明细</h3>
            <div className="row between mb-3">
              <span className="sm muted">商品单价</span>
              <span className="sm">¥{amountYuan}</span>
            </div>
            <div className="row between mb-3">
              <span className="sm muted">数量</span>
              <span className="sm">× 1</span>
            </div>
            <div
              style={{ height: 1, background: 'var(--color-border)', margin: 'var(--space-4) 0' }}
            />
            <div className="row between mb-6">
              <span style={{ fontWeight: 'var(--weight-semibold)' }}>合计</span>
              <span className="price">¥{amountYuan}</span>
            </div>

            {submitError && (
              <p
                className="sm mb-3"
                role="alert"
                data-testid="order-preview-submit-error"
                style={{ color: 'var(--color-destructive)' }}
              >
                {submitError}
              </p>
            )}

            <button
              type="button"
              className="btn btn-primary btn-lg btn-block mb-3"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? '提交中…' : '提交订单'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => navigate(-1)}
            >
              返回
            </button>
            <p className="xs muted mt-4">提交后将进入收银台完成支付,订单 15 分钟内有效。</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function formatYuanNoSymbol(cents: number): string {
  return (cents / 100).toFixed(2);
}
